import { PlusIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import SessionCard from "./SessionCard";
import { usePendingSessionIds } from "@/hooks/useSessionRequestState";
import type { AskSession } from "@/api/client";
import type { Podcast } from "@/hooks/usePodcasts";

interface SessionSidebarProps {
  sessions: AskSession[];
  podcasts: Podcast[];
  activeSessionId: string | null;
  onSelectSession: (session: AskSession) => void;
  onCreateSession: () => void;
  onDeleteSession: (session: AskSession) => void;
  isLoading: boolean;
}

export default function SessionSidebar({
  sessions,
  podcasts,
  activeSessionId,
  onSelectSession,
  onCreateSession,
  onDeleteSession,
  isLoading,
}: SessionSidebarProps) {
  // Create a map for quick podcast lookup
  const podcastMap = new Map(podcasts.map((p) => [p.id, p]));

  // Get set of session IDs with pending requests from localStorage
  const pendingSessionIds = usePendingSessionIds();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Sessions
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {sessions.length}/3
          </span>
        </div>
        <button
          onClick={onCreateSession}
          className={cn(
            "w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg",
            "text-sm font-medium",
            theme.buttons.primary,
          )}
        >
          <PlusIcon className="w-4 h-4" />
          New Session
        </button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-primary-orange dark:border-t-primary-orange-dark rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8 px-4">
            <div className="text-4xl mb-3">💬</div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              No active sessions
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Start a new conversation to begin chatting with your podcasts.
            </p>
          </div>
        ) : (
          sessions.map((session, index) => (
            <SessionCard
              key={session.session_id ?? `session-${index}`}
              session={session}
              podcast={podcastMap.get(session.podcast_slug) ?? null}
              isActive={session.session_id === activeSessionId}
              hasPendingRequest={pendingSessionIds.has(session.session_id)}
              onClick={() => onSelectSession(session)}
              onDelete={() => onDeleteSession(session)}
            />
          ))
        )}
      </div>

      {/* Footer hint */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Sessions expire after 4 hours
        </p>
      </div>
    </div>
  );
}
